import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  MoreVert as MoreVertIcon,
  Note as NoteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderStatus, OrderStatusFilters } from "../../../types/admin";

interface OrderStatusTableProps {
  statuses: OrderStatus[];
  totalCount: number;
  filters: OrderStatusFilters;
  onFiltersChange: (filters: OrderStatusFilters) => void;
  onView: (status: OrderStatus) => void;
  onEdit: (status: OrderStatus) => void;
  onDelete: (id: number) => void;
  onToggleActive: (id: number) => void;
  onBulkToggleActive: (statusIds: number[], isActive: boolean) => void;
  loading?: boolean;
  selectedStatuses: number[];
  onSelectionChange: (selectedIds: number[]) => void;
}

const OrderStatusTable: React.FC<OrderStatusTableProps> = ({
  statuses,
  totalCount,
  filters,
  onFiltersChange,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onBulkToggleActive,
  loading = false,
  selectedStatuses,
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    status: OrderStatus,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(status);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStatus(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedStatus
  };

  const handleDeleteConfirm = async () => {
    if (!selectedStatus) return;
    try {
      await onDelete(selectedStatus.id);
      setSnackbar({
        open: true,
        message: t("admin.orderStatus.deleteSuccess"),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("admin.orderStatus.deleteFailed"),
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedStatus(null); // Clear selected status after delete
    }
  };

  const handleToggleActive = async () => {
    if (!selectedStatus) return;
    try {
      await onToggleActive(selectedStatus.id);
      setSnackbar({
        open: true,
        message: selectedStatus.isActive ? t("admin.orderStatus.deactivateSuccess") : t("admin.orderStatus.activateSuccess"),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("admin.orderStatus.toggleFailed"),
        severity: "error",
      });
    } finally {
      handleMenuClose();
    }
  };

  const handleBulkToggleActive = async (isActive: boolean) => {
    try {
      await onBulkToggleActive(selectedStatuses, isActive);
      setSnackbar({
        open: true,
        message: isActive
          ? t("admin.orderStatus.bulkActivateSuccess", { count: selectedStatuses.length })
          : t("admin.orderStatus.bulkDeactivateSuccess", { count: selectedStatuses.length }),
        severity: "success",
      });
      onSelectionChange([]);
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("admin.orderStatus.bulkUpdateFailed"),
        severity: "error",
      });
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onSelectionChange(statuses.map((s) => s.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectStatus = (statusId: number, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedStatuses, statusId]);
    } else {
      onSelectionChange(selectedStatuses.filter((id) => id !== statusId));
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    onFiltersChange({ ...filters, pageNumber: newPage + 1 });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onFiltersChange({
      ...filters,
      pageSize: parseInt(event.target.value, 10),
      pageNumber: 1,
    });
  };

  const getStatusColor = (color: string) => {
    return color.startsWith("#") ? color : `#${color}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isAllSelected =
    statuses.length > 0 && selectedStatuses.length === statuses.length;
  const isIndeterminate =
    selectedStatuses.length > 0 && selectedStatuses.length < statuses.length;

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={loading}
                />
              </TableCell>
              <TableCell>{t("admin.orderStatus.status")}</TableCell>
              <TableCell>{t("admin.orderStatus.name")}</TableCell>
              <TableCell>{t("admin.orderStatus.sortOrder")}</TableCell>
              <TableCell>{t("admin.orderStatus.requirements")}</TableCell>
              <TableCell>{t("admin.orderStatus.usage")}</TableCell>
              <TableCell>{t("admin.orderStatus.created")}</TableCell>
              <TableCell>{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : statuses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.noStatusesFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              statuses.map((status) => (
                <TableRow key={status.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedStatuses.includes(status.id)}
                      onChange={(e) =>
                        handleSelectStatus(status.id, e.target.checked)
                      }
                      disabled={loading}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: getStatusColor(status.color),
                        }}
                      />
                      <Typography variant="body2" fontWeight="medium">
                        {status.displayName}
                      </Typography>
                      <Chip
                        label={status.isActive ? t("admin.orderStatus.active") : t("admin.orderStatus.inactive")}
                        size="small"
                        color={status.isActive ? "success" : "error"}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {status.name}
                    </Typography>
                    {status.description && (
                      <Typography variant="caption" color="text.secondary">
                        {status.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DragIndicatorIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {status.sortOrder}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {status.requiresTrackingNumber && (
                        <Tooltip title={t("admin.orderStatus.requiresTrackingNumber")}>
                          <LocalShippingIcon fontSize="small" color="info" />
                        </Tooltip>
                      )}
                      {status.requiresNote && (
                        <Tooltip title={t("admin.orderStatus.requiresNote")}>
                          <NoteIcon fontSize="small" color="info" />
                        </Tooltip>
                      )}
                      {!status.requiresTrackingNumber &&
                        !status.requiresNote && (
                          <Typography variant="caption" color="text.secondary">
                            {t("admin.orderStatus.none")}
                          </Typography>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {status.orderCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("admin.orderStatus.orders")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(status.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title={t("admin.orderStatus.viewDetails")}>
                        <IconButton
                          size="small"
                          onClick={() => onView(status)}
                          disabled={loading}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("admin.orderStatus.editStatus")}>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(status)}
                          disabled={loading}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={status.isActive ? t("admin.orderStatus.deactivate") : t("admin.orderStatus.activate")}
                      >
                        <IconButton
                          size="small"
                          onClick={() => onToggleActive(status.id)}
                          disabled={loading}
                        >
                          {status.isActive ? (
                            <ToggleOffIcon />
                          ) : (
                            <ToggleOnIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, status)}
                        disabled={loading}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={filters.pageSize || 20}
        page={(filters.pageNumber || 1) - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* Bulk Actions */}
      {selectedStatuses.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            display: "flex",
            gap: 1,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleBulkToggleActive(true)}
            disabled={loading}
          >
            {t("admin.orderStatus.activateCount", { count: selectedStatuses.length })}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => handleBulkToggleActive(false)}
            disabled={loading}
          >
            {t("admin.orderStatus.deactivateCount", { count: selectedStatuses.length })}
          </Button>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => onView(selectedStatus!)}>
          <VisibilityIcon sx={{ mr: 1 }} />
          {t("admin.orderStatus.viewDetails")}
        </MenuItem>
        <MenuItem onClick={() => onEdit(selectedStatus!)}>
          <EditIcon sx={{ mr: 1 }} />
          {t("admin.orderStatus.editStatus")}
        </MenuItem>
        <MenuItem onClick={handleToggleActive}>
          {selectedStatus?.isActive ? (
            <ToggleOffIcon sx={{ mr: 1 }} />
          ) : (
            <ToggleOnIcon sx={{ mr: 1 }} />
          )}
          {selectedStatus?.isActive ? t("admin.orderStatus.deactivate") : t("admin.orderStatus.activate")}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1 }} />
          {t("common.delete")}
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("admin.orderStatus.deleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("admin.orderStatus.deleteConfirmation", { name: selectedStatus?.displayName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrderStatusTable;
